/* =========================================================================
   estate-access-gate.tsx — module_access entry gate for the estate.

   Applies ONLY to Supabase-authenticated sessions: a successful
   `module_access` query with no `campus_lease_studio` row renders
   AccessDenied (fail-closed), while a query ERROR fails open with a
   console.warn — RLS stays the real data-security boundary and this gate
   only controls entry/navigation, matching the sibling apps. Passcode
   fallback mode (local/dev convenience) is never gated.
   ========================================================================= */

import React, { useEffect, useState } from "react";
import { useAuth } from "./auth-context";
import { supabase, isSupabaseConfigured } from "./supabase-client";
import { THIS_MODULE, openEstateApp } from "./estate-sso";
import { Wordmark } from "./wordmark";

type GateState = "checking" | "granted" | "denied";

function AccessDenied({ email }: Readonly<{ email?: string }>) {
  return (
    <main className="auth-page" aria-labelledby="access-denied-title">
      <section className="auth-card" role="alert">
        <div className="auth-logo">
          <Wordmark size="md" />
        </div>
        <h1 id="access-denied-title">No access to TENURE</h1>
        <p>
          {email ? `${email} is` : "Your account is"} signed in to Stratosfyre,
          but this module hasn&apos;t been enabled for it yet.
        </p>
        <p className="p-small">
          Ask your Stratosfyre administrator to grant the{" "}
          <code>campus_lease_studio</code> module, or head back to the estate.
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void openEstateApp("website")}
        >
          Back to Stratosfyre
        </button>
      </section>
    </main>
  );
}

export function EstateAccessGate({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const auth = useAuth();
  // Gate only real Supabase sessions; passcode fallback stays ungated.
  const gated =
    !auth.usePasscodeFallback && isSupabaseConfigured() && !!auth.user;
  const userId = auth.user?.id;
  const [state, setState] = useState<GateState>(gated ? "checking" : "granted");

  useEffect(() => {
    if (!gated || !userId) {
      setState("granted");
      return;
    }
    let cancelled = false;
    setState("checking");

    supabase
      .from("module_access")
      .select("module")
      .eq("user_id", userId)
      .eq("module", THIS_MODULE)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          // Fail open: module_access gates entry only; RLS remains the
          // data boundary. Denying on transient errors would lock real
          // users out of their own data.
          console.warn(
            "[estate-access] module_access query failed — failing open:",
            error.message,
          );
          setState("granted");
          return;
        }
        setState(data ? "granted" : "denied");
      });

    return () => {
      cancelled = true;
    };
  }, [gated, userId]);

  if (state === "denied") return <AccessDenied email={auth.email} />;
  // While checking, render nothing gate-specific — the app shell's own
  // loading states cover the (fast) single-row query.
  if (state === "checking") return null;
  return <>{children}</>;
}
